import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { crewService } from '@services/crew';
import { CREW_QUERY_KEY } from './useCrew';
import { CREWS_BY_USER_QUERY_KEY } from './useCrews';
import { EMPLOYEES_QUERY_KEY } from '@hooks/api/employees/useEmployees';
import { getErrorMessage } from '@utils/errorMessage';
import { useTranslation } from '@hooks/useTranslation';

export type AssignEmployeesArgs = {
  crewId: string;
  employeeIds: string[];
};

export const useAssignEmployeesToCrew = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['crew', 'assignEmployees'],
    mutationFn: async ({ crewId, employeeIds }: AssignEmployeesArgs) => {
      if (!crewId) throw new Error('crewId is required');
      if (!employeeIds || employeeIds.length === 0)
        throw new Error('employeeIds is required');
      return await crewService.assignEmployeesToCrew(crewId, employeeIds);
    },
    onSuccess: (crew) => {
      toast.success(tActionMessages.crewMembersUpdatedSuccessfully);
      queryClient.invalidateQueries({ queryKey: CREW_QUERY_KEY(crew.crewId) });
      queryClient.invalidateQueries({ queryKey: CREWS_BY_USER_QUERY_KEY });
      // Membership changed, so every employee's `crews` list is now stale.
      queryClient.invalidateQueries({ queryKey: EMPLOYEES_QUERY_KEY });
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, tActionMessages.requestError));
    },
  });
};

export default useAssignEmployeesToCrew;
